/**
 * Working-resolution math.
 *
 * Vectorization runs at a bounded resolution to keep the optimization loop
 * responsive; the result is scaled back to the source dimensions on export, so
 * the exact scale factor has to be recoverable.
 */

import type { CropRect } from '$lib/types'

export interface FittedSize {
  width: number
  height: number
  /** working size = source size * scale */
  scale: number
}

/**
 * Scales a source size so its longest side is at most `longSide`.
 *
 * Images smaller than the target are left alone: upscaling adds no detail for
 * the skeletonizer to find and only makes the optimization slower.
 */
export function fitToLongSide(
  sourceWidth: number,
  sourceHeight: number,
  longSide: number,
): FittedSize {
  if (!isPositiveFinite(sourceWidth) || !isPositiveFinite(sourceHeight)) {
    throw new RangeError('Source dimensions must be positive finite numbers')
  }
  if (!isPositiveFinite(longSide)) {
    throw new RangeError('Target long side must be a positive finite number')
  }

  const longest = Math.max(sourceWidth, sourceHeight)
  if (longest <= longSide) {
    return { width: Math.round(sourceWidth), height: Math.round(sourceHeight), scale: 1 }
  }

  const scale = longSide / longest
  return {
    // Guard against a rounding-to-zero on extreme aspect ratios.
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
    scale,
  }
}

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0
}

/**
 * Clamps a crop rectangle to the image and rounds it to whole pixels.
 *
 * Returns `null` for anything that would not survive the trip — off the image
 * entirely, or thinner than a pixel after rounding — so callers can treat "no
 * usable crop" and "no crop requested" the same way, rather than defending
 * against a zero-width canvas further down.
 */
export function normalizeCrop(
  crop: CropRect | null | undefined,
  imageWidth: number,
  imageHeight: number,
): CropRect | null {
  if (!crop) return null
  if (!isPositiveFinite(imageWidth) || !isPositiveFinite(imageHeight)) return null

  const left = Math.max(0, Math.min(imageWidth, Math.round(crop.x)))
  const top = Math.max(0, Math.min(imageHeight, Math.round(crop.y)))
  const right = Math.max(left, Math.min(imageWidth, Math.round(crop.x + crop.width)))
  const bottom = Math.max(top, Math.min(imageHeight, Math.round(crop.y + crop.height)))

  const width = right - left
  const height = bottom - top
  if (width < 1 || height < 1) return null
  // A crop covering everything is the same as no crop, and saying so keeps the
  // "was this cropped?" question answerable by identity.
  if (left === 0 && top === 0 && width === imageWidth && height === imageHeight) return null
  return { x: left, y: top, width, height }
}
